import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { containsNoValues } from 'src/util/object.helpers'
import {
  checkValidations,
  requiredRule
} from 'src/util/validation/commonValidation.helpers'
import {
  DEFAULT_CONTAINER_STATE,
  QR_CODE_PROCESS_STEP,
  REWARD_NFT_PROCESS_STEP,
  REWARD_POINTS_PROCESS_STEP
} from '../constants/containerStates'

import ReceiveReward from '../components/overview/ReceiveReward'
import CameraBlocked from '../components/popups/qrCode/CameraBlocked'
import ReceiveRewardByQrCode from '../components/popups/qrCode/ReceiveRewardByQrCode'
import CodeExpired from '../components/popups/reward/CodeExpired'
import CodeNotExist from '../components/popups/reward/CodeNotExist'
import CodeTooManyTries from '../components/popups/reward/CodeTooManyTries'
import CodeUsed from '../components/popups/reward/CodeUsed'
import {
  changeRewardsInputAndErrorState,
  changeRewardsState,
  validateRewardCode
} from '../store/actions/rewards'
import { REWARDS_PROFILE_DISPATCH } from '../store/reducers/rewards/rewards'
import { extractCodeOrInputText } from '../util/string.helpers'
import { validRewardCodeRule } from '../util/validation/validation.helpers'
import { usePreviousState } from './usePreviousState'

const useReceiveReward = ({ t, rewardsProfile, tokenProfile }) => {
  const dispatch = useDispatch()

  useEffect(() => {
    if (rewardsProfile.codeValidityState) {
      setPopupState(rewardsProfile.codeValidityState)
    }
  }, [rewardsProfile.codeValidityState])

  useEffect(() => {
    resetCodeState()
    return () => {
      resetCodeState()
    }
  }, [])

  const [popupState, setPopupState] = useState(DEFAULT_CONTAINER_STATE.NONE)
  const prevPopupState = usePreviousState(popupState)

  const resetRewardCode = () => {
    dispatch(changeRewardsState(REWARDS_PROFILE_DISPATCH.REWARD_CODE, ''))
  }

  const handleDispatchAction = (property, value) => {
    dispatch(changeRewardsInputAndErrorState(property, value))
  }

  const handleNoPopup = () => {
    resetRewardCode()
    dispatch(changeRewardsState(REWARDS_PROFILE_DISPATCH.ERROR, {}))
    dispatch(
      changeRewardsState(REWARDS_PROFILE_DISPATCH.CODE_VALIDITY_STATE, '')
    )
    setPopupState(DEFAULT_CONTAINER_STATE.NONE)
  }

  const validateOrConfirmCode = (rewardCode) => {
    const validRewardCode = validRewardCodeRule(rewardCode)
    if (validRewardCode) {
      resetCodeState()
      dispatch(validateRewardCode(validRewardCode))
    } else {
      setPopupState(REWARD_POINTS_PROCESS_STEP.WRONG_REWARD_CODE)
    }
  }

  const handleSubmitReceiveReward = () => {
    const { rewardCode } = rewardsProfile
    dispatch(changeRewardsState(REWARDS_PROFILE_DISPATCH.ERROR, {}))
    const errors = checkValidations({
      rewardCode: { value: rewardCode, rules: [requiredRule] }
    })
    if (containsNoValues(errors)) {
      validateOrConfirmCode(rewardCode)
    } else {
      dispatch(changeRewardsState(REWARDS_PROFILE_DISPATCH.ERROR, errors))
    }
  }

  const handlePopupSubmit = () => {
    switch (popupState) {
      case REWARD_NFT_PROCESS_STEP.NFT_RECEIVED:
      case REWARD_POINTS_PROCESS_STEP.TOO_MANY_TRIES:
        handleNoPopup()
        break

      case REWARD_POINTS_PROCESS_STEP.WRONG_REWARD_CODE:
        resetRewardCode()
        const changePopupState =
          prevPopupState === QR_CODE_PROCESS_STEP.SCAN_CODE
            ? QR_CODE_PROCESS_STEP.SCAN_CODE
            : ''
        dispatch(
          changeRewardsState(
            REWARDS_PROFILE_DISPATCH.CODE_VALIDITY_STATE,
            changePopupState
          )
        )
        setPopupState(changePopupState)

        break
      case REWARD_POINTS_PROCESS_STEP.CODE_ALREADY_USED:
      case REWARD_POINTS_PROCESS_STEP.CODE_EXPIRED:
        resetRewardCode()
        handleNoPopup()
        break

      default:
        handleNoPopup()
        break
    }
  }

  const handleQrCodeClick = () => {
    setPopupState(QR_CODE_PROCESS_STEP.SCAN_CODE)
  }

  const handleCancelQrCode = () => {
    handleNoPopup()
  }

  const handleQrCodeError = () => {
    setPopupState(QR_CODE_PROCESS_STEP.CAMERA_BLOCKED)
  }

  const resetCodeState = () => {
    dispatch(
      changeRewardsState(
        REWARDS_PROFILE_DISPATCH.CODE_VALIDITY_STATE,
        DEFAULT_CONTAINER_STATE.NONE
      )
    )
  }

  const handleQrCodeScan = (text) => {
    if (text && !rewardsProfile.loading) {
      const rewardCode = extractCodeOrInputText(text)
      if (rewardCode) {
        validateOrConfirmCode(rewardCode)
      } else {
        setPopupState(REWARD_POINTS_PROCESS_STEP.WRONG_REWARD_CODE)
      }
    }
  }

  const renderPopup = () => {
    switch (popupState) {
      case REWARD_POINTS_PROCESS_STEP.WRONG_REWARD_CODE:
        return <CodeNotExist onSubmit={handlePopupSubmit} t={t} />

      case REWARD_POINTS_PROCESS_STEP.TOO_MANY_TRIES:
        return <CodeTooManyTries onSubmit={handlePopupSubmit} t={t} />

      case REWARD_POINTS_PROCESS_STEP.CODE_EXPIRED:
        return <CodeExpired onSubmit={handlePopupSubmit} t={t} />

      case REWARD_POINTS_PROCESS_STEP.CODE_ALREADY_USED:
        return <CodeUsed onSubmit={handlePopupSubmit} t={t} />

      case QR_CODE_PROCESS_STEP.SCAN_CODE:
        return (
          <ReceiveRewardByQrCode
            onCancel={handleCancelQrCode}
            onError={handleQrCodeError}
            onScan={handleQrCodeScan}
            t={t}
          />
        )

      case QR_CODE_PROCESS_STEP.CAMERA_BLOCKED:
        return <CameraBlocked onCancel={handleCancelQrCode} t={t} />

      default:
        break
    }
  }

  const renderCard = () => {
    return (
      <ReceiveReward
        t={t}
        dispatchAction={handleDispatchAction}
        rewardsProfile={rewardsProfile}
        onSubmit={handleSubmitReceiveReward}
        onQrCodeClick={handleQrCodeClick}
        symbol={tokenProfile.symbol}
      />
    )
  }
  return [renderCard, renderPopup, popupState]
}

export { useReceiveReward }
